import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  createTheme,
  Drawer,
  Menu,
  Modal,
  NavLink,
  NumberInput,
  Paper,
  Pagination,
  PasswordInput,
  Popover,
  SegmentedControl,
  Select,
  MultiSelect,
  Switch,
  Table,
  Tabs,
  Textarea,
  TextInput,
  Tooltip,
  type MantineColorsTuple,
} from '@mantine/core';

/**
 * The platform console look: FormTags ink on white, quiet gray canvas, color only for status.
 * Dense controls (36px), 8px corners on controls, 12px on panels, borders instead of shadows.
 */
const ink: MantineColorsTuple = ['#F4F5F7', '#E7E8EC', '#CDD0D7', '#B1B5BF', '#979CA8', '#7C8290', '#5F6672', '#3E434C', '#24272D', '#111216'];

export const FONT = '"IBM Plex Sans Arabic", "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif';
export const MONO = 'ui-monospace, "SF Mono", "Cascadia Code", Menlo, Consolas, monospace';

export const platformTheme = createTheme({
  fontFamily: FONT,
  fontFamilyMonospace: MONO,
  headings: { fontFamily: FONT, fontWeight: '600', sizes: { h1: { fontSize: '1.375rem', lineHeight: '1.35' }, h2: { fontSize: '1.125rem', lineHeight: '1.4' }, h3: { fontSize: '1rem', lineHeight: '1.45' } } },
  colors: { ink },
  primaryColor: 'ink',
  primaryShade: 9,
  black: '#0F1115',
  defaultRadius: 'md',
  radius: { xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px' },
  fontSizes: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem', lg: '1rem', xl: '1.125rem' },
  spacing: { xs: '6px', sm: '10px', md: '14px', lg: '20px', xl: '28px' },
  shadows: {
    xs: '0 1px 2px rgba(16,18,22,.05)',
    sm: '0 1px 2px rgba(16,18,22,.06), 0 1px 3px rgba(16,18,22,.06)',
    md: '0 4px 12px rgba(16,18,22,.08), 0 1px 3px rgba(16,18,22,.06)',
    lg: '0 12px 32px rgba(16,18,22,.12)',
    xl: '0 24px 48px rgba(16,18,22,.16)',
  },
  cursorType: 'pointer',
  focusRing: 'auto',
  components: {
    Button: Button.extend({ defaultProps: { size: 'sm', radius: 'md' }, styles: { root: { fontWeight: 500 } } }),
    ActionIcon: ActionIcon.extend({ defaultProps: { variant: 'subtle', color: 'gray', radius: 'md' } }),
    TextInput: TextInput.extend({ defaultProps: { size: 'sm' } }),
    PasswordInput: PasswordInput.extend({ defaultProps: { size: 'sm' } }),
    NumberInput: NumberInput.extend({ defaultProps: { size: 'sm', hideControls: true } }),
    Textarea: Textarea.extend({ defaultProps: { size: 'sm', autosize: true, minRows: 2 } }),
    Select: Select.extend({ defaultProps: { size: 'sm', allowDeselect: false, comboboxProps: { shadow: 'md' } } }),
    MultiSelect: MultiSelect.extend({ defaultProps: { size: 'sm', comboboxProps: { shadow: 'md' } } }),
    Checkbox: Checkbox.extend({ defaultProps: { size: 'sm', radius: 'sm' } }),
    Switch: Switch.extend({ defaultProps: { size: 'sm', withThumbIndicator: false, color: 'teal.6' } }),
    SegmentedControl: SegmentedControl.extend({ defaultProps: { size: 'sm', radius: 'md' } }),
    Badge: Badge.extend({ defaultProps: { variant: 'light', radius: 'sm', size: 'md' }, styles: { root: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 } } }),
    Card: Card.extend({ defaultProps: { withBorder: true, radius: 'lg', padding: 'lg', shadow: 'none' } }),
    Paper: Paper.extend({ defaultProps: { radius: 'lg' } }),
    Modal: Modal.extend({ defaultProps: { radius: 'lg', centered: true, overlayProps: { backgroundOpacity: 0.35, blur: 2 }, padding: 'lg' }, styles: { title: { fontWeight: 600, fontSize: '1rem' } } }),
    Drawer: Drawer.extend({ defaultProps: { overlayProps: { backgroundOpacity: 0.35, blur: 2 } } }),
    Menu: Menu.extend({ defaultProps: { shadow: 'md', radius: 'md', position: 'bottom-end', withinPortal: true } }),
    Popover: Popover.extend({ defaultProps: { shadow: 'md', radius: 'md' } }),
    Tooltip: Tooltip.extend({ defaultProps: { openDelay: 300, radius: 'sm', fz: 'xs' } }),
    Tabs: Tabs.extend({ styles: { tab: { fontWeight: 500 } } }),
    NavLink: NavLink.extend({ styles: { root: { borderRadius: 'var(--mantine-radius-md)' } } }),
    Table: Table.extend({ defaultProps: { verticalSpacing: 'xs', horizontalSpacing: 'md', highlightOnHover: true } }),
    Pagination: Pagination.extend({ defaultProps: { size: 'sm', radius: 'md' } }),
  },
});
